import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ukCommon from './locales/uk/common.json';
import ukNav from './locales/uk/nav.json';
import ukLogin from './locales/uk/login.json';
import ukUsers from './locales/uk/users.json';
import ukDashboard from './locales/uk/dashboard.json';
import ukChannels from './locales/uk/channels.json';
import ukMessages from './locales/uk/messages.json';
import ukAccounts from './locales/uk/accounts.json';
import ukBrands from './locales/uk/brands.json';
import ukCategories from './locales/uk/categories.json';
import ukStats from './locales/uk/stats.json';
import ukInsights from './locales/uk/insights.json';

import ruCommon from './locales/ru/common.json';
import ruNav from './locales/ru/nav.json';
import ruLogin from './locales/ru/login.json';
import ruUsers from './locales/ru/users.json';
import ruDashboard from './locales/ru/dashboard.json';
import ruChannels from './locales/ru/channels.json';
import ruMessages from './locales/ru/messages.json';
import ruAccounts from './locales/ru/accounts.json';
import ruBrands from './locales/ru/brands.json';
import ruCategories from './locales/ru/categories.json';
import ruStats from './locales/ru/stats.json';
import ruInsights from './locales/ru/insights.json';

import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enLogin from './locales/en/login.json';
import enUsers from './locales/en/users.json';
import enDashboard from './locales/en/dashboard.json';
import enChannels from './locales/en/channels.json';
import enMessages from './locales/en/messages.json';
import enAccounts from './locales/en/accounts.json';
import enBrands from './locales/en/brands.json';
import enCategories from './locales/en/categories.json';
import enStats from './locales/en/stats.json';
import enInsights from './locales/en/insights.json';

export const SUPPORTED_LANGUAGES = ['uk', 'ru', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'uk';
export const LANGUAGE_COOKIE = 'pc_lang';

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  uk: 'Українська',
  ru: 'Русский',
  en: 'English',
};

const resources = {
  uk: {
    common: ukCommon,
    nav: ukNav,
    login: ukLogin,
    users: ukUsers,
    dashboard: ukDashboard,
    channels: ukChannels,
    messages: ukMessages,
    accounts: ukAccounts,
    brands: ukBrands,
    categories: ukCategories,
    stats: ukStats,
    insights: ukInsights,
  },
  ru: {
    common: ruCommon,
    nav: ruNav,
    login: ruLogin,
    users: ruUsers,
    dashboard: ruDashboard,
    channels: ruChannels,
    messages: ruMessages,
    accounts: ruAccounts,
    brands: ruBrands,
    categories: ruCategories,
    stats: ruStats,
    insights: ruInsights,
  },
  en: {
    common: enCommon,
    nav: enNav,
    login: enLogin,
    users: enUsers,
    dashboard: enDashboard,
    channels: enChannels,
    messages: enMessages,
    accounts: enAccounts,
    brands: enBrands,
    categories: enCategories,
    stats: enStats,
    insights: enInsights,
  },
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    defaultNS: 'common',
    ns: Object.keys(resources.uk),
    interpolation: { escapeValue: false },
    detection: {
      order: ['cookie', 'localStorage', 'navigator'],
      caches: ['cookie', 'localStorage'],
      lookupCookie: LANGUAGE_COOKIE,
      cookieMinutes: 60 * 24 * 365, // 1 year
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    react: { useSuspense: false },
  });

export function changeLanguage(lang: SupportedLanguage): Promise<unknown> {
  return i18n.changeLanguage(lang);
}

export default i18n;
